// Deterministic form-advisor logic.
// STRICT rules only — no legal advice, no probabilistic AI.
// Given a set of answers, returns the list of USCIS forms typically filled
// for that scenario, so the user (or their attorney) can decide what to file.

// Answer keys used across questions
export const ANSWER_KEYS = {
  goal: "goal",                     // green_card | work_visa | travel | asylum | humanitarian | ead_only
  location: "location",             // inside_us | outside_us
  gc_basis: "gc_basis",             // employment | family | asylum_1yr | vawa | sij | trafficking
  employment_class: "employment_class", // eb1a | eb1b | eb1c | eb2niw | eb2 | eb3
  work_visa_type: "work_visa_type", // h1b | l1 | o1 | tn | e2 | h2b
  travel_status: "travel_status",   // pending_i485 | permanent_resident | refugee_asylee
  ead_basis: "ead_basis",           // pending_i485 | f1_opt | asylum_pending | asylee
  has_dependents: "has_dependents", // yes | no
};

// Deterministic mapping: (answers) => { forms: [{code, reason_pt, reason_es, reason_en}], package_id?: string }
export function computeSuggestion(answers) {
  const { goal, location, gc_basis, employment_class, work_visa_type, travel_status, ead_basis, has_dependents } = answers;

  const forms = [];
  let packageId = null;

  const push = (code, reason_pt, reason_es, reason_en) => {
    forms.push({ code, reason_pt, reason_es, reason_en });
  };

  // ---------- GREEN CARD (Adjustment of Status when inside US) ----------
  if (goal === "green_card") {
    if (location === "inside_us") {
      // AOS-based flow → I-485 + I-765 + I-131 (always the triad)
      // Underlying petition depends on basis
      if (gc_basis === "employment") {
        push("I-140", "Petição base para categoria empregatícia (EB-1/EB-2/EB-3).", "Petición base para categoría de empleo (EB-1/EB-2/EB-3).", "Underlying employment petition (EB-1/EB-2/EB-3).");
        push("I-485", "Ajuste de status para Residente Permanente estando nos EUA.", "Ajuste de estatus a Residente Permanente estando en EE.UU.", "Adjustment of status to Permanent Resident while inside the US.");
        push("I-765", "EAD c(9) — autorização de trabalho enquanto I-485 pendente.", "EAD c(9) — autorización de empleo mientras I-485 esté pendiente.", "EAD c(9) — work authorization while I-485 is pending.");
        push("I-131", "Advance Parole — permissão para viajar durante I-485 pendente.", "Advance Parole — permiso para viajar durante I-485 pendiente.", "Advance Parole — travel permission while I-485 pending.");
        if (employment_class === "eb2niw") packageId = "AOS_COMBO_EB2NIW";
        else if (employment_class === "eb3") packageId = "AOS_COMBO_EB3";
      } else if (gc_basis === "family") {
        push("I-485", "Ajuste de status baseado em petição familiar (I-130 anexada separadamente).", "Ajuste de estatus basado en petición familiar (I-130 se anexa por separado).", "Adjustment of status based on family petition (I-130 filed separately).");
        push("I-765", "EAD c(9) — autorização de trabalho enquanto I-485 pendente.", "EAD c(9) — autorización de empleo mientras I-485 esté pendiente.", "EAD c(9) — work authorization while I-485 pending.");
        push("I-131", "Advance Parole durante I-485 pendente.", "Advance Parole durante I-485 pendiente.", "Advance Parole while I-485 pending.");
        packageId = "FAMILY_AOS";
      } else if (gc_basis === "asylum_1yr") {
        push("I-485", "Ajuste de status um ano após concessão de asilo.", "Ajuste de estatus un año después de la concesión de asilo.", "Adjustment of status one year after asylum grant.");
      } else if (gc_basis === "vawa") {
        push("I-360", "Autopetição VAWA (vítima de abuso por familiar cidadão/RP).", "Autopetición VAWA (víctima de abuso por familiar ciudadano/RP).", "VAWA self-petition (victim of abuse by USC/LPR relative).");
        push("I-485", "Ajuste de status subsequente à autopetição VAWA aprovada.", "Ajuste de estatus subsecuente a la autopetición VAWA aprobada.", "Adjustment of status after approved VAWA self-petition.");
      } else if (gc_basis === "sij") {
        push("I-360", "Classificação como Menor Imigrante Especial (SIJ).", "Clasificación como Menor Inmigrante Especial (SIJ).", "Special Immigrant Juvenile classification.");
        push("I-485", "Ajuste de status após SIJ aprovado.", "Ajuste de estatus tras SIJ aprobada.", "Adjustment of status after SIJ approved.");
      } else if (gc_basis === "trafficking") {
        push("I-914", "Status T de não-imigrante para vítimas de tráfico humano.", "Estatus T de no inmigrante para víctimas de trata.", "T nonimmigrant status for trafficking victims.");
        push("I-485", "Ajuste de status após 3 anos de T-1 contínuo.", "Ajuste de estatus tras 3 años continuos en T-1.", "Adjustment of status after 3 continuous years in T-1.");
      }
    } else {
      // outside_us — consular processing (out of scope for this platform)
      if (gc_basis === "employment") {
        push("I-140", "Petição base — processamento consular ocorre fora deste sistema.", "Petición base — el procesamiento consular ocurre fuera de este sistema.", "Underlying petition — consular processing happens outside this system.");
      }
    }
  }

  // ---------- WORK VISA (nonimmigrant) ----------
  if (goal === "work_visa") {
    push("I-129", `Petição para visto de trabalho temporário${work_visa_type ? ` (${work_visa_type.toUpperCase()})` : ""}.`, `Petición para visa de trabajo temporal${work_visa_type ? ` (${work_visa_type.toUpperCase()})` : ""}.`, `Petition for nonimmigrant worker${work_visa_type ? ` (${work_visa_type.toUpperCase()})` : ""}.`);
  }

  // ---------- ASYLUM ----------
  if (goal === "asylum") {
    push("I-589", "Pedido de asilo e retenção de remoção.", "Solicitud de asilo y retención de expulsión.", "Asylum and withholding of removal application.");
    push("I-765", "EAD c(8) — trabalho enquanto asilo estiver pendente (após 150 dias).", "EAD c(8) — empleo mientras el asilo está pendiente (tras 150 días).", "EAD c(8) — work authorization while asylum is pending (after 150 days).");
  }

  // ---------- HUMANITARIAN ----------
  if (goal === "humanitarian") {
    if (gc_basis === "vawa") push("I-360", "Autopetição VAWA.", "Autopetición VAWA.", "VAWA self-petition.");
    if (gc_basis === "sij") push("I-360", "Menor Imigrante Especial (SIJ).", "Menor Inmigrante Especial (SIJ).", "Special Immigrant Juvenile (SIJ).");
    if (gc_basis === "trafficking") push("I-914", "Status T para vítima de tráfico humano.", "Estatus T para víctima de trata.", "T status for trafficking victim.");
  }

  // ---------- EAD ONLY ----------
  if (goal === "ead_only") {
    push("I-765", "Autorização de trabalho (EAD).", "Autorización de empleo (EAD).", "Work authorization (EAD).");
  }

  // ---------- TRAVEL ----------
  if (goal === "travel") {
    push("I-131", "Documento de viagem / Advance Parole conforme seu status atual.", "Documento de viaje / Advance Parole según su estatus actual.", "Travel document / Advance Parole based on your current status.");
  }

  // ---------- DEPENDENTS notice (does not add forms, just note) ----------
  const notes = [];
  if (has_dependents === "yes") {
    notes.push({
      pt: "Você indicou dependentes. No Pacote Combo, cada dependente recebe seu próprio I-485/I-765/I-131.",
      es: "Indicó dependientes. En el Paquete Combo, cada dependiente recibe su propio I-485/I-765/I-131.",
      en: "You indicated dependents. In the Combo Package, each dependent gets their own I-485/I-765/I-131.",
    });
  }
  if (location === "outside_us" && goal === "green_card") {
    notes.push({
      pt: "Você está fora dos EUA — o processamento consular (DS-260) NÃO é gerado por este sistema. Este sistema atende principalmente casos de Ajuste de Status dentro dos EUA.",
      es: "Está fuera de EE.UU. — el procesamiento consular (DS-260) NO se genera por este sistema. Este sistema atiende principalmente casos de Ajuste de Estatus dentro de EE.UU.",
      en: "You are outside the US — consular processing (DS-260) is NOT generated by this system. This platform primarily serves Adjustment of Status cases inside the US.",
    });
  }

  return { forms, packageId, notes };
}

// Question tree config (declarative)
// Each question can be conditionally shown via `showIf(answers)`
export const QUESTIONS = [
  {
    id: "goal",
    label_pt: "Qual é o objetivo principal?",
    label_es: "¿Cuál es el objetivo principal?",
    label_en: "What is the primary goal?",
    options: [
      { value: "green_card", label_pt: "Green Card (Residência Permanente)", label_es: "Green Card (Residencia Permanente)", label_en: "Green Card (Permanent Residency)" },
      { value: "work_visa", label_pt: "Visto de trabalho temporário", label_es: "Visa de trabajo temporal", label_en: "Temporary work visa" },
      { value: "asylum", label_pt: "Asilo político", label_es: "Asilo político", label_en: "Political asylum" },
      { value: "humanitarian", label_pt: "Proteção humanitária (VAWA / SIJ / T)", label_es: "Protección humanitaria (VAWA / SIJ / T)", label_en: "Humanitarian protection (VAWA / SIJ / T)" },
      { value: "ead_only", label_pt: "Somente EAD (autorização de trabalho)", label_es: "Solo EAD (autorización de empleo)", label_en: "EAD only (work authorization)" },
      { value: "travel", label_pt: "Documento de viagem / Advance Parole", label_es: "Documento de viaje / Advance Parole", label_en: "Travel document / Advance Parole" },
    ],
  },
  {
    id: "location",
    label_pt: "Onde a pessoa está atualmente?",
    label_es: "¿Dónde está la persona actualmente?",
    label_en: "Where is the person currently located?",
    showIf: (a) => a.goal === "green_card",
    options: [
      { value: "inside_us", label_pt: "Dentro dos EUA", label_es: "Dentro de EE.UU.", label_en: "Inside the US" },
      { value: "outside_us", label_pt: "Fora dos EUA", label_es: "Fuera de EE.UU.", label_en: "Outside the US" },
    ],
  },
  {
    id: "gc_basis",
    label_pt: "Qual a base para a Green Card?",
    label_es: "¿Cuál es la base para la Green Card?",
    label_en: "What is the basis for the Green Card?",
    showIf: (a) => a.goal === "green_card" || a.goal === "humanitarian",
    options: [
      { value: "employment", label_pt: "Baseada em emprego (EB-1/EB-2/EB-3)", label_es: "Basada en empleo (EB-1/EB-2/EB-3)", label_en: "Employment-based (EB-1/EB-2/EB-3)" },
      { value: "family", label_pt: "Baseada em família (cônjuge/pai/filho)", label_es: "Basada en familia (cónyuge/padre/hijo)", label_en: "Family-based (spouse/parent/child)" },
      { value: "asylum_1yr", label_pt: "Asilo concedido há mais de 1 ano", label_es: "Asilo concedido hace más de 1 año", label_en: "Asylum granted more than 1 year ago" },
      { value: "vawa", label_pt: "VAWA (vítima de abuso)", label_es: "VAWA (víctima de abuso)", label_en: "VAWA (abuse victim)" },
      { value: "sij", label_pt: "SIJ (menor imigrante especial)", label_es: "SIJ (menor inmigrante especial)", label_en: "SIJ (special immigrant juvenile)" },
      { value: "trafficking", label_pt: "Vítima de tráfico humano (Status T)", label_es: "Víctima de trata (Estatus T)", label_en: "Trafficking victim (T status)" },
    ],
  },
  {
    id: "employment_class",
    label_pt: "Qual categoria empregatícia?",
    label_es: "¿Cuál categoría de empleo?",
    label_en: "Which employment category?",
    showIf: (a) => a.goal === "green_card" && a.gc_basis === "employment",
    options: [
      { value: "eb1a", label_pt: "EB-1A (habilidade extraordinária)", label_es: "EB-1A (habilidad extraordinaria)", label_en: "EB-1A (extraordinary ability)" },
      { value: "eb1b", label_pt: "EB-1B (pesquisador destacado)", label_es: "EB-1B (investigador destacado)", label_en: "EB-1B (outstanding researcher)" },
      { value: "eb1c", label_pt: "EB-1C (executivo multinacional)", label_es: "EB-1C (ejecutivo multinacional)", label_en: "EB-1C (multinational executive)" },
      { value: "eb2niw", label_pt: "EB-2 NIW (interesse nacional)", label_es: "EB-2 NIW (interés nacional)", label_en: "EB-2 NIW (national interest)" },
      { value: "eb2", label_pt: "EB-2 (grau avançado)", label_es: "EB-2 (grado avanzado)", label_en: "EB-2 (advanced degree)" },
      { value: "eb3", label_pt: "EB-3 (profissional / qualificado)", label_es: "EB-3 (profesional / calificado)", label_en: "EB-3 (professional / skilled)" },
    ],
  },
  {
    id: "work_visa_type",
    label_pt: "Qual tipo de visto de trabalho?",
    label_es: "¿Qué tipo de visa de trabajo?",
    label_en: "Which work visa type?",
    showIf: (a) => a.goal === "work_visa",
    options: [
      { value: "h1b", label_pt: "H-1B (ocupação especializada)", label_es: "H-1B (ocupación especializada)", label_en: "H-1B (specialty occupation)" },
      { value: "l1", label_pt: "L-1 (transferência intra-empresa)", label_es: "L-1 (transferencia intra-empresa)", label_en: "L-1 (intra-company transfer)" },
      { value: "o1", label_pt: "O-1 (habilidade extraordinária)", label_es: "O-1 (habilidad extraordinaria)", label_en: "O-1 (extraordinary ability)" },
      { value: "tn", label_pt: "TN (NAFTA — Canadá/México)", label_es: "TN (NAFTA — Canadá/México)", label_en: "TN (NAFTA — Canada/Mexico)" },
      { value: "e2", label_pt: "E-2 (investidor)", label_es: "E-2 (inversor)", label_en: "E-2 (investor)" },
      { value: "h2b", label_pt: "H-2B (não-agrícola temporário)", label_es: "H-2B (no agrícola temporal)", label_en: "H-2B (temporary non-agricultural)" },
    ],
  },
  {
    id: "has_dependents",
    label_pt: "Há dependentes (cônjuge/filhos) para incluir no processo?",
    label_es: "¿Hay dependientes (cónyuge/hijos) para incluir en el proceso?",
    label_en: "Are there dependents (spouse/children) to include?",
    showIf: (a) => a.goal === "green_card" || a.goal === "humanitarian",
    options: [
      { value: "yes", label_pt: "Sim", label_es: "Sí", label_en: "Yes" },
      { value: "no", label_pt: "Não", label_es: "No", label_en: "No" },
    ],
  },
];
